import { Filters, Sorter } from '../interfaces/Api';
import { Analyst } from '../interfaces/Analyst';
import axios from 'axios';
import BaseService from './BaseService';
import { Client } from '../interfaces/Client';
import Configuration from './ServiceConfig';

interface Response {
	data: Client[];
	totalCount: number;
}

class ClientsService extends BaseService {
	config: Configuration;
	path: string;
	token: string | null;

	constructor() {
		super();
		this.config = new Configuration();
		this.path = `${this.config.baseurl}${'clients/'}`;
		this.token = localStorage.getItem('token');
	}

	private parseDataTable(results: any[]): Client[] {
		return results.map(object => {
			return {
				key: object.id,
				name: object.name,
				partner: object.globalPartner.name,
				activeProjects: object.activeProjects,
				status: object.status,
			};
		});
	}

	async getAllWithoutPagination(): Promise<Response | void> {
		return axios
			.get(this.path, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => {
				return {
					data: this.parseDataTable(response.data.rows),
					totalCount: response.data.count,
				};
			})
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async getAll(filters: Filters, sorter: Sorter, limit: number = 10, offset: number = 0): Promise<Response | void> {
		let params = {};

		if (filters && filters.name) {
			params = { ...params, ...filters };
		}

		if (sorter && sorter.sort) {
			params = { ...params, ...sorter };
		}

		params = { ...params, limit, offset };

		return axios
			.get(this.path, {
				params,
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return {
					data: this.parseDataTable(response.data.rows),
					totalCount: response.data.count,
				};
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async get(id: number): Promise<Response | void> {
		return axios
			.get(`${this.path}${id}`, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return response.data;
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async getByPartnerId(partnerId: number | undefined): Promise<Response | void> {
		let params = {};

		if (partnerId) {
			params = { ...params, globalPartnerId: partnerId };
		}

		return axios
			.get(this.path, {
				params,
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return {
					data: this.parseDataTable(response.data.rows),
					totalCount: response.data.count,
				};
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async create(item: Client): Promise<Response | void> {
		return axios
			.post(this.path, item, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return response.data;
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}

				throw err;
			});
	}

	async delete(id: number) {
		return axios
			.delete(`${this.path}${id}`, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return response;
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async update(id: number, client: Client): Promise<Response | void> {
		return axios
			.patch(`${this.path}${id}`, client, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return response.data;
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async getAnalystsByClientId(clientId: number): Promise<Analyst[] | void> {
		return axios
			.get(`${this.path}${clientId}/analysts`, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => response.data)
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}
}

export default ClientsService;
